import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Users, Building2, Target, Activity, TrendingUp,
  Search, RefreshCw, ChevronRight, Copy, Trash2, UserX, UserCheck,
  LogOut, Mail, User, Plus, X, Eye, EyeOff, KeyRound, AtSign, Handshake
} from "lucide-react";
import AdminPartnersSection from "../components/admin/AdminPartnersSection";
import { clearSession, getSessionToken } from '../utils/authSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboardPage = () => {
  const PAGE_SIZE = 25;
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [clubsTotal, setClubsTotal] = useState(0);
  const [usersTotal, setUsersTotal] = useState(0);
  const [clubsPage, setClubsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [clubsSort, setClubsSort] = useState("newest");
  const [usersSort, setUsersSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [emailUser, setEmailUser] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [newClub, setNewClub] = useState({
    name: "",
    sport: "",
    admin_name: "",
    admin_email: "",
    admin_password: ""
  });

  useEffect(() => {
    const stored = localStorage.getItem("mygoal-admin");
    if (!stored) {
      navigate("/admin");
      return;
    }
    setAdmin(JSON.parse(stored));
  }, [navigate]);

  useEffect(() => {
    if (admin) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, activeTab, clubsPage, usersPage, clubsSort, usersSort]);

  useEffect(() => {
    setUsersPage(1);
  }, [accountTypeFilter, searchQuery, usersSort]);

  useEffect(() => {
    setClubsPage(1);
  }, [clubsSort]);

  const getSortParams = (sortValue) => {
    switch (sortValue) {
      case "oldest":
        return { sort_by: "oldest", sort_order: "asc" };
      case "largest":
        return { sort_by: "largest", sort_order: "desc" };
      case "name":
        return { sort_by: "name", sort_order: "asc" };
      case "most_sessions":
        return { sort_by: "most_sessions", sort_order: "desc" };
      case "most_goals":
        return { sort_by: "most_goals", sort_order: "desc" };
      default:
        return { sort_by: "newest", sort_order: "desc" };
    }
  };

  const fetchAdminPage = async (endpoint, extraParams = {}) => {
    const params = new URLSearchParams({
      admin_id: admin.id,
      limit: String(PAGE_SIZE),
      ...Object.fromEntries(
        Object.entries(extraParams).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      ),
    });

    const response = await axios.get(`${BACKEND_URL}/api/admin/${endpoint}?${params.toString()}`);
    return response.data || { items: [], total_count: 0, has_more: false, skip: 0, limit: PAGE_SIZE };
  };

  const fetchData = async () => {
    if (!admin) return;
    setLoading(true);

    try {
      if (activeTab === "overview" || !stats) {
        const statsRes = await axios.get(`${BACKEND_URL}/api/admin/stats?admin_id=${admin.id}`);
        setStats(statsRes.data);
      }

      if (activeTab === "clubs") {
        const clubsRes = await fetchAdminPage("clubs", {
          skip: (clubsPage - 1) * PAGE_SIZE,
          ...getSortParams(clubsSort),
        });
        setClubs(clubsRes.items || []);
        setClubsTotal(clubsRes.total_count || 0);
      }

      if (activeTab === "users") {
        const usersRes = await fetchAdminPage("users", {
          skip: (usersPage - 1) * PAGE_SIZE,
          account_type: accountTypeFilter,
          search: searchQuery,
          ...getSortParams(usersSort),
        });
        setUsers(usersRes.items || []);
        setUsersTotal(usersRes.total_count || 0);
      }

      // Partners tab: AdminPartnersSection fetches its own data — no parent work needed
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Session expired");
        handleLogout();
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${getSessionToken()}` } }).catch(() => {});
    clearSession();
    navigate("/admin");
  };

  const handleGenerateCode = async (clubId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/clubs/${clubId}/generate-code?admin_id=${admin.id}`
      );
      toast.success(`New code: ${res.data.connect_code}`);
      navigator.clipboard.writeText(res.data.connect_code);
      fetchData();
    } catch {
      toast.error("Failed to generate code");
    }
  };

  const handleCreateClub = async () => {
    if (!newClub.name || !newClub.admin_email || !newClub.admin_name || !newClub.admin_password) {
      toast.error("Please fill all required fields");
      return;
    }
    if (newClub.admin_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/clubs/create?admin_id=${admin.id}`,
        newClub
      );
      toast.success(`Club "${res.data.club.name}" created! Code: ${res.data.club.connect_code}`);
      navigator.clipboard.writeText(res.data.club.connect_code);
      setShowCreateClub(false);
      setNewClub({ name: "", sport: "", admin_name: "", admin_email: "", admin_password: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create club");
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/deactivate?admin_id=${admin.id}`);
      toast.success("User deactivated");
      fetchData();
    } catch {
      toast.error("Failed to deactivate user");
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/activate?admin_id=${admin.id}`);
      toast.success("User activated");
      fetchData();
    } catch {
      toast.error("Failed to activate user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("PERMANENTLY delete this user and all their data? This cannot be undone.")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}?admin_id=${admin.id}`);
      toast.success("User deleted");
      setSelectedUser(null);
      fetchData();
    } catch {
      toast.error("Failed to delete user");
    }
  };

  const openResetPassword = (user) => {
    setResetUser(user);
    setResetPassword("");
    setResetPasswordVisible(false);
  };

  const closeResetPassword = () => {
    setResetUser(null);
    setResetPassword("");
    setResetPasswordVisible(false);
  };

  const handleResetPassword = async () => {
    if (!resetUser) return;
    if (!resetPassword || resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResetting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${resetUser.id}/reset-password?admin_id=${admin.id}`,
        { new_password: resetPassword }
      );
      toast.success(`Password reset for ${resetUser.email}`);
      closeResetPassword();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const openChangeEmail = (user) => {
    setEmailUser(user);
    setNewEmail(user.email || "");
  };

  const closeChangeEmail = () => {
    setEmailUser(null);
    setNewEmail("");
  };

  const handleChangeEmail = async () => {
    if (!emailUser) return;
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (trimmed === (emailUser.email || "").toLowerCase()) {
      toast.error("New email is the same as current");
      return;
    }
    setChangingEmail(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${emailUser.id}/change-email?admin_id=${admin.id}`,
        { new_email: trimmed }
      );
      toast.success(`Email updated to ${trimmed}`);
      closeChangeEmail();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change email");
    } finally {
      setChangingEmail(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const clubsStart = clubsTotal === 0 ? 0 : ((clubsPage - 1) * PAGE_SIZE) + 1;
  const clubsEnd = Math.min(clubsPage * PAGE_SIZE, clubsTotal);
  const usersStart = usersTotal === 0 ? 0 : ((usersPage - 1) * PAGE_SIZE) + 1;
  const usersEnd = Math.min(usersPage * PAGE_SIZE, usersTotal);
  const clubsHasPrev = clubsPage > 1;
  const clubsHasNext = clubsPage * PAGE_SIZE < clubsTotal;
  const usersHasPrev = usersPage > 1;
  const usersHasNext = usersPage * PAGE_SIZE < usersTotal;

  const tabs = [
    { id: "overview", label: "Overview", icon: TrendingUp },
    { id: "clubs", label: "Clubs", icon: Building2 },
    { id: "users", label: "Users", icon: Users },
    { id: "partners", label: "Partners", icon: Handshake },
  ];

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="bg-[#1a1a2e] border-b border-white/10 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Shield className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-sm md:text-lg">Admin Panel</h1>
              <p className="text-white/40 text-xs hidden sm:block">{admin.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1 md:gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-xs md:text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-[#1a1a2e]/50 border-b border-white/10 overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex gap-1 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-red-500 text-white"
                      : "border-transparent text-white/50 hover:text-white/70"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        {loading && !stats ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-white/50" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === "overview" && stats && (
              <div className="space-y-4 md:space-y-6">
                <h2 className="text-lg md:text-xl font-bold">Platform Overview</h2>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                  <StatCard icon={Users} label="Total Users" value={stats.total_users} color="blue" />
                  <StatCard icon={Building2} label="Clubs" value={stats.total_clubs} color="purple" />
                  <StatCard icon={Activity} label="Sessions" value={stats.total_sessions} color="green" />
                  <StatCard icon={Target} label="Goals" value={stats.total_goals} color="orange" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
                  <StatCard icon={User} label="Athletes" value={stats.total_athletes} color="cyan" />
                  <StatCard icon={Users} label="Coaches" value={stats.total_coaches} color="pink" />
                  <StatCard icon={TrendingUp} label="New (7d)" value={stats.recent_signups_7d} color="yellow" />
                </div>
              </div>
            )}

            {/* Clubs Tab */}
            {activeTab === "clubs" && (
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg md:text-xl font-bold">All Clubs ({clubsTotal || clubs.length})</h2>
                    <p className="text-xs text-white/40 mt-1" data-testid="admin-clubs-range-label">
                      Showing {clubsStart}-{clubsEnd} of {clubsTotal || clubs.length}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={clubsSort}
                      onChange={(e) => setClubsSort(e.target.value)}
                      className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                      data-testid="admin-clubs-sort-select"
                    >
                      <option value="newest">Newest clubs</option>
                      <option value="oldest">Oldest clubs</option>
                      <option value="largest">Largest clubs</option>
                      <option value="name">Club name A-Z</option>
                    </select>
                    <button
                      onClick={() => setShowCreateClub(true)}
                      className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-xs md:text-sm font-medium hover:opacity-90"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">New Club</span>
                    </button>
                    <button
                      onClick={fetchData}
                      className="p-2 rounded-lg bg-white/5 hover:bg-white/10"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {clubs.map((club) => (
                    <div
                      key={club.id}
                      className="p-3 md:p-4 rounded-xl bg-[#1a1a2e] border border-white/10 hover:border-white/20 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-sm md:text-base truncate">{club.name}</h3>
                            <p className="text-white/50 text-xs md:text-sm">{club.sport || "No sport"}</p>
                            <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1 md:mt-2 text-xs text-white/40">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {club.member_count} members
                              </span>
                              {club.admin && (
                                <span className="flex items-center gap-1 truncate max-w-[150px]">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  {club.admin.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                          <div className="text-left sm:text-right">
                            <p className="text-xs text-white/40">Code</p>
                            <div className="flex items-center gap-1">
                              <code className="text-xs md:text-sm font-mono text-green-400">
                                {club.connect_code || "None"}
                              </code>
                              {club.connect_code && (
                                <button
                                  onClick={() => copyToClipboard(club.connect_code)}
                                  className="p-1 hover:bg-white/10 rounded"
                                >
                                  <Copy className="w-3 h-3 text-white/40" />
                                </button>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleGenerateCode(club.id)}
                            className="px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-xs font-medium hover:opacity-90 whitespace-nowrap"
                          >
                            New Code
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {clubs.length === 0 && (
                    <div className="text-center py-12 text-white/40">
                      No clubs found
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 pt-2" data-testid="admin-clubs-pagination">
                  <button
                    onClick={() => setClubsPage((page) => Math.max(1, page - 1))}
                    disabled={!clubsHasPrev}
                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                    data-testid="admin-clubs-prev-button"
                  >
                    Previous
                  </button>
                  <p className="text-xs text-white/40" data-testid="admin-clubs-page-label">Page {clubsPage}</p>
                  <button
                    onClick={() => setClubsPage((page) => page + 1)}
                    disabled={!clubsHasNext}
                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                    data-testid="admin-clubs-next-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === "users" && (
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg md:text-xl font-bold">Users ({usersTotal || users.length})</h2>
                    <p className="text-xs text-white/40 mt-1" data-testid="admin-users-range-label">
                      Showing {usersStart}-{usersEnd} of {usersTotal || users.length}
                    </p>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search name or email..."
                      className="w-full pl-10 pr-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                    />
                  </div>
                  <select
                    value={accountTypeFilter}
                    onChange={(e) => setAccountTypeFilter(e.target.value)}
                    className="px-3 md:px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                  >
                    <option value="">All Types</option>
                    <option value="athlete">Athletes</option>
                    <option value="coach">Coaches</option>
                    <option value="club">Club Admins</option>
                  </select>
                  <select
                    value={usersSort}
                    onChange={(e) => setUsersSort(e.target.value)}
                    className="px-3 md:px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                    data-testid="admin-users-sort-select"
                  >
                    <option value="newest">Newest users</option>
                    <option value="oldest">Oldest users</option>
                    <option value="most_sessions">Most sessions</option>
                    <option value="most_goals">Most goals</option>
                    <option value="name">Name A-Z</option>
                  </select>
                  <button
                    onClick={fetchData}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-sm font-medium hover:opacity-90"
                    data-testid="admin-users-search-button"
                  >
                    Search
                  </button>
                </div>

                {/* Users List */}
                <div className="space-y-2">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className={`p-3 md:p-4 rounded-xl bg-[#1a1a2e] border transition-colors cursor-pointer ${
                        selectedUser?.id === user.id
                          ? "border-red-500/50"
                          : "border-white/10 hover:border-white/20"
                      }`}
                      onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-3 min-w-0">
                          <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            user.account_type === "athlete" ? "bg-blue-500/20" :
                            user.account_type === "coach" ? "bg-green-500/20" :
                            user.account_type === "club" ? "bg-purple-500/20" : "bg-white/10"
                          }`}>
                            <User className={`w-4 h-4 md:w-5 md:h-5 ${
                              user.account_type === "athlete" ? "text-blue-400" :
                              user.account_type === "coach" ? "text-green-400" :
                              user.account_type === "club" ? "text-purple-400" : "text-white/40"
                            }`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                              <h3 className="font-medium text-sm md:text-base truncate">{user.name}</h3>
                              <span className={`px-1.5 md:px-2 py-0.5 rounded-full text-xs ${
                                user.account_type === "athlete" ? "bg-blue-500/20 text-blue-400" :
                                user.account_type === "coach" ? "bg-green-500/20 text-green-400" :
                                user.account_type === "club" ? "bg-purple-500/20 text-purple-400" : ""
                              }`}>
                                {user.account_type}
                              </span>
                              {user.is_active === false && (
                                <span className="px-1.5 md:px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <p className="text-white/40 text-xs md:text-sm truncate">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4 text-xs text-white/40 flex-shrink-0">
                          <span className="hidden sm:inline">{user.session_count} sessions</span>
                          <span className="hidden sm:inline">{user.goal_count} goals</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${
                            selectedUser?.id === user.id ? "rotate-90" : ""
                          }`} />
                        </div>
                      </div>

                      {/* Expanded User Actions */}
                      {selectedUser?.id === user.id && (
                        <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-white/10">
                          <div className="flex flex-wrap items-center gap-2 mb-2 sm:hidden text-xs text-white/40">
                            <span>{user.session_count} sessions</span>
                            <span>•</span>
                            <span>{user.goal_count} goals</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {user.is_active === false ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleActivateUser(user.id); }}
                                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-green-500/20 text-green-400 text-xs hover:bg-green-500/30"
                              >
                                <UserCheck className="w-3 h-3 md:w-4 md:h-4" />
                                Activate
                              </button>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeactivateUser(user.id); }}
                                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-yellow-500/20 text-yellow-400 text-xs hover:bg-yellow-500/30"
                              >
                                <UserX className="w-3 h-3 md:w-4 md:h-4" />
                                Deactivate
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); openResetPassword(user); }}
                              className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-blue-500/20 text-blue-300 text-xs hover:bg-blue-500/30"
                              data-testid={`admin-reset-password-button-${user.id}`}
                            >
                              <KeyRound className="w-3 h-3 md:w-4 md:h-4" />
                              Reset password
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openChangeEmail(user); }}
                              className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-purple-500/20 text-purple-300 text-xs hover:bg-purple-500/30"
                              data-testid={`admin-change-email-button-${user.id}`}
                            >
                              <AtSign className="w-3 h-3 md:w-4 md:h-4" />
                              Change email
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id); }}
                              className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-red-500/20 text-red-400 text-xs hover:bg-red-500/30"
                            >
                              <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                              Delete
                            </button>
                            <div className="ml-auto text-xs text-white/30 hidden sm:block">
                              ID: {user.id.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {users.length === 0 && (
                    <div className="text-center py-12 text-white/40">
                      No users found
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 pt-2" data-testid="admin-users-pagination">
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setUsersPage((page) => Math.max(1, page - 1));
                    }}
                    disabled={!usersHasPrev}
                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                    data-testid="admin-users-prev-button"
                  >
                    Previous
                  </button>
                  <p className="text-xs text-white/40" data-testid="admin-users-page-label">Page {usersPage}</p>
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setUsersPage((page) => page + 1);
                    }}
                    disabled={!usersHasNext}
                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                    data-testid="admin-users-next-button"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {activeTab === "partners" && (
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Handshake className="w-5 h-5 text-blue-300" />
                  <h2 className="text-lg md:text-xl font-bold">Our Partners</h2>
                </div>
                <p className="text-xs text-white/50 mb-5 max-w-2xl">
                  Manage the partner logos shown to users in the &quot;Our Partners&quot; widget. Set global partners (default for all users) and/or per-club partners. Toggle global override to force every user to see the global set.
                </p>
                <AdminPartnersSection admin={admin} />
              </div>
            )}
          </>
        )}
      </main>

      {/* Create Club Modal */}
      {showCreateClub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md bg-[#1a1a2e] rounded-2xl border border-white/10 p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-bold">Create New Club</h2>
              <button
                onClick={() => setShowCreateClub(false)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-xs md:text-sm text-white/60 mb-1.5">Club Name *</label>
                <input
                  type="text"
                  value={newClub.name}
                  onChange={(e) => setNewClub({ ...newClub, name: e.target.value })}
                  placeholder="e.g., Elite Sports Academy"
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm text-white/60 mb-1.5">Sport</label>
                <input
                  type="text"
                  value={newClub.sport}
                  onChange={(e) => setNewClub({ ...newClub, sport: e.target.value })}
                  placeholder="e.g., Football, Basketball"
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                />
              </div>

              <div className="pt-2 md:pt-4 border-t border-white/10">
                <p className="text-xs md:text-sm text-white/40 mb-3">Club Admin Account</p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs md:text-sm text-white/60 mb-1.5">Admin Name *</label>
                    <input
                      type="text"
                      value={newClub.admin_name}
                      onChange={(e) => setNewClub({ ...newClub, admin_name: e.target.value })}
                      placeholder="Club administrator name"
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm text-white/60 mb-1.5">Admin Email *</label>
                    <input
                      type="email"
                      value={newClub.admin_email}
                      onChange={(e) => setNewClub({ ...newClub, admin_email: e.target.value })}
                      placeholder="admin@club.com"
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm text-white/60 mb-1.5">Admin Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newClub.admin_password}
                        onChange={(e) => setNewClub({ ...newClub, admin_password: e.target.value })}
                        placeholder="Min 6 characters"
                        className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-red-500/50 focus:outline-none pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateClub}
                disabled={creating}
                className="w-full py-2.5 md:py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm md:text-base mt-2"
              >
                {creating ? "Creating..." : "Create Club"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={closeResetPassword}
          data-testid="admin-reset-password-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/20 text-blue-300">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Reset Password</h3>
                  <p className="text-xs text-white/50 mt-0.5 break-all">{resetUser.email}</p>
                </div>
              </div>
              <button
                onClick={closeResetPassword}
                className="text-white/40 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-white/50 mb-3">
              Set a new password for <span className="text-white/80">{resetUser.name || resetUser.email}</span>. The user will need to log in with this password from now on.
            </p>

            <label className="block text-xs text-white/60 mb-1.5">New password *</label>
            <div className="relative">
              <input
                type={resetPasswordVisible ? "text" : "password"}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-blue-500/50 focus:outline-none pr-10"
                data-testid="admin-reset-password-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setResetPasswordVisible((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40"
              >
                {resetPasswordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={closeResetPassword}
                disabled={resetting}
                className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm text-white/80 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetting || !resetPassword || resetPassword.length < 6}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                data-testid="admin-reset-password-confirm"
              >
                {resetting ? "Resetting..." : "Reset password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Email Modal */}
      {emailUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={closeChangeEmail}
          data-testid="admin-change-email-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-purple-500/20 text-purple-300">
                  <AtSign className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Change Email</h3>
                  <p className="text-xs text-white/50 mt-0.5 break-all">
                    Current: {emailUser.email}
                  </p>
                </div>
              </div>
              <button
                onClick={closeChangeEmail}
                className="text-white/40 hover:text-white"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-white/50 mb-3">
              Set a new login email for <span className="text-white/80">{emailUser.name || emailUser.email}</span>. They will need to use this email to log in from now on.
            </p>

            <label className="block text-xs text-white/60 mb-1.5">New email *</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@email.com"
              className="w-full px-3 md:px-4 py-2 md:py-3 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-purple-500/50 focus:outline-none"
              data-testid="admin-change-email-input"
              autoFocus
            />

            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={closeChangeEmail}
                disabled={changingEmail}
                className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm text-white/80 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeEmail}
                disabled={changingEmail || !newEmail}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                data-testid="admin-change-email-confirm"
              >
                {changingEmail ? "Saving..." : "Save email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: "from-blue-500/20 to-blue-600/20 text-blue-400",
    purple: "from-purple-500/20 to-purple-600/20 text-purple-400",
    green: "from-green-500/20 to-green-600/20 text-green-400",
    orange: "from-orange-500/20 to-orange-600/20 text-orange-400",
    cyan: "from-cyan-500/20 to-cyan-600/20 text-cyan-400",
    pink: "from-pink-500/20 to-pink-600/20 text-pink-400",
    yellow: "from-yellow-500/20 to-yellow-600/20 text-yellow-400",
  };

  return (
    <div className={`p-3 md:p-4 rounded-xl bg-gradient-to-br ${colorClasses[color]} border border-white/5`}>
      <div className="flex items-center gap-2 md:gap-3">
        <Icon className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-white/60 text-xs truncate">{label}</p>
          <p className="text-xl md:text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
